import { useEffect, useId, useRef, type PointerEvent } from 'react';

type TextHoverEffectProps = {
  text: string;
  className?: string;
};

export function TextHoverEffect({ text, className = '' }: TextHoverEffectProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const revealGradientRef = useRef<SVGRadialGradientElement>(null);
  const identifier = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const colorGradientId = `wordmark-color-${identifier}`;
  const revealGradientId = `wordmark-reveal-${identifier}`;
  const maskId = `wordmark-mask-${identifier}`;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        svg.dataset.visible = 'true';
        observer.disconnect();
      },
      { threshold: 0.35 },
    );

    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  const moveReveal = (event: PointerEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;

    revealGradientRef.current?.setAttribute('cx', `${x}%`);
    revealGradientRef.current?.setAttribute('cy', `${y}%`);
  };

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1000 340"
      role="img"
      aria-label={text}
      preserveAspectRatio="xMidYMid meet"
      onPointerMove={moveReveal}
      className={`text-hover-effect ${className}`.trim()}
    >
      <defs>
        <linearGradient id={colorGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#d4ff36" />
          <stop offset="38%" stopColor="#f4ffd0" />
          <stop offset="68%" stopColor="#7ad8c8" />
          <stop offset="100%" stopColor="#d4ff36" />
        </linearGradient>
        <radialGradient ref={revealGradientRef} id={revealGradientId} cx="50%" cy="50%" r="24%">
          <stop offset="0%" stopColor="white" />
          <stop offset="42%" stopColor="white" stopOpacity="0.92" />
          <stop offset="100%" stopColor="black" stopOpacity="0" />
        </radialGradient>
        <mask id={maskId}>
          <rect width="100%" height="100%" fill={`url(#${revealGradientId})`} />
        </mask>
      </defs>

      <text className="text-hover-effect__base" x="500" y="252" textAnchor="middle">
        {text}
      </text>
      <text className="text-hover-effect__trace" x="500" y="252" textAnchor="middle">
        {text}
      </text>
      <text
        className="text-hover-effect__reveal"
        x="500"
        y="252"
        textAnchor="middle"
        stroke={`url(#${colorGradientId})`}
        mask={`url(#${maskId})`}
      >
        {text}
      </text>
    </svg>
  );
}
